import React, { useEffect, useState } from "react";
import { Send } from "lucide-react";
import TextInput from "../../ui/TextInput";
import Dropdown from "../../ui/SelectDropdown";
import TextArea from "../../ui/TextArea";
import AutomatedMessages from "./AutomatedMessages";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import { RootState } from "../../../redux/app/store";
import { cleanObject } from "../../../helpers";
import {
  clearOrders,
  fetchAllOrders,
} from "../../../redux/features/orderSlice";
import {
  sendWhatsAppSingleNotification,
  sendWhatsAppBulkNotification,
  sendSingleNotification,
  sendBulkNotification,
  sendPromoNotification,
} from "../../../redux/features/notificationSlice";

const targetUserTypes = [
  { label: "BOTH", value: "BOTH" },
  { label: "VENDOR", value: "VENDOR" },
  { label: "BUYER", value: "BUYER" },
];

// const countryCodes = [
//   { label: "+234", value: "+234" },
//   { label: "+1", value: "+1" },
//   { label: "+44", value: "+44" },
//   { label: "+91", value: "+91" },
// ];

// Termi WhatsApp template types
const templateTypes = [
  { label: "Recommendations", value: "RECOMMENDATIONS" },
  { label: "Update", value: "UPDATE" },
  { label: "Intasida", value: "INTASIDA" },
];

type TabType = "WhatsApp" | "Push" | "Anonymous Push" | "Automated Messages";

const PushNotificationsPage = () => {
  const { orders } = useAppSelector((state: RootState) => state.orders);
  const [activeTab, setActiveTab] = useState<TabType>("WhatsApp");
  const [refinedOrders, setRefinedOrders] = useState<any[]>([]);
  const [filter, setFilter] = useState("");

  // WhatsApp Payloads
  const [whatsappSinglePayload, setWhatsappSinglePayload] = useState({
    // countryCode: "+234",
    phoneNumber: "",
    templateType: "",
  });
  const [whatsappBulkPayload, setWhatsappBulkPayload] = useState({
    templateType: "",
  });

  // Push Payloads (for registered users)
  const [singlePayload, setSinglePayload] = useState({
    title: "",
    contractId: "",
    text: "",
    notificationType: "INFO", // fixed
  });
  const [bulkPayload, setBulkPayload] = useState({
    title: "",
    message: "",
    targetUserType: "ALL",
    notificationType: "INFO", // fixed
  });

  // Anonymous Push Payload
  const [promoPayload, setPromoPayload] = useState({
    // installIds: ["string"], // fixed - matches Swagger format
    title: "",
    message: "",
    notificationType: "INFO", // fixed
    limit: 100, // fixed - max installs to send to
    metadata: {
      screen: "signup",
      cta: "complete_profile",
    }, // fixed
  });

  const {
    sendingWhatsAppSingle,
    sendingWhatsAppBulk,
    sendingSingle,
    sendingBulk,
    sendingPromo,
  } = useAppSelector((state: RootState) => state.notifications);
  const dispatch = useAppDispatch();

  // WhatsApp Single Notification Submit
  const handleWhatsAppSingleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        phoneNumber: whatsappSinglePayload.phoneNumber,
        templateType: whatsappSinglePayload.templateType,
        data: {},
      };
      await dispatch(sendWhatsAppSingleNotification(payload)).unwrap();
      // reset form after success
      setWhatsappSinglePayload({
        // countryCode: "+234",
        phoneNumber: "",
        templateType: "",
      });
    } catch (err) {
      console.error("Failed to send WhatsApp single notification", err);
    }
  };

  // WhatsApp Bulk Notification Submit
  const handleWhatsAppBulkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        templateType: whatsappBulkPayload.templateType,
      };
      await dispatch(sendWhatsAppBulkNotification(payload)).unwrap();
      // reset form after success
      setWhatsappBulkPayload({
        templateType: "",
      });
    } catch (err) {
      console.error("Failed to send WhatsApp bulk notification", err);
    }
  };

  const handleSingleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await dispatch(
        sendSingleNotification(cleanObject(singlePayload)),
      ).unwrap();
      // reset form after success
      setSinglePayload({
        title: "",
        contractId: "",
        text: "",
        notificationType: "INFO",
      });
    } catch (err) {
      console.error("Failed to send single notification", err);
    }
  };

  const handleBulkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await dispatch(sendBulkNotification(cleanObject(bulkPayload))).unwrap();
      // reset form after success
      setBulkPayload({
        title: "",
        message: "",
        targetUserType: "ALL",
        notificationType: "INFO",
      });
    } catch (err) {
      console.error("Failed to send bulk notification", err);
    }
  };

  // Anonymous Push Notification Submit
  const handlePromoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await dispatch(sendPromoNotification(promoPayload)).unwrap();
      // reset form after success
      setPromoPayload({
        // installIds: ["string"],
        title: "",
        message: "",
        notificationType: "APP_PROMOTION",
        limit: 100,
        metadata: {
          screen: "signup",
          cta: "complete_profile",
        },
      });
    } catch (err) {
      console.error("Failed to send Anonymous Push notification", err);
    }
  };

  useEffect(() => {
    const filteredOrders: any = orders?.data?.data?.map((x) => ({
      label: `${x?.contract?.title} (${x?.contract?.contractCode})`,
      value: {
        orderId: x?.id,
        contractId: x?.contract?.id,
      },
    }));
    setRefinedOrders(filteredOrders);
  }, [orders]);

  useEffect(() => {
    const params = cleanObject({
      limit: 10,
      page: 1,
      filter,
    });
    dispatch(clearOrders());
    dispatch(fetchAllOrders(params));
  }, [dispatch, filter]);

  return (
    <div className="p-6 space-y-10">
      {/* Page Title */}
      <div className="flex items-center gap-2 mb-6">
        <h1 className="text-2xl font-bold">Send Notifications</h1>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab("WhatsApp")}
          className={`px-6 py-3 text-sm font-medium transition-colors ${
            activeTab === "WhatsApp"
              ? "text-primary border-b-2 border-primary"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          WhatsApp
        </button>
        <button
          onClick={() => setActiveTab("Push")}
          className={`px-6 py-3 text-sm font-medium transition-colors ${
            activeTab === "Push"
              ? "text-primary border-b-2 border-primary"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Push
        </button>
        <button
          onClick={() => setActiveTab("Anonymous Push")}
          className={`px-6 py-3 text-sm font-medium transition-colors ${
            activeTab === "Anonymous Push"
              ? "text-primary border-b-2 border-primary"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Anonymous Push
        </button>
        <button
          onClick={() => setActiveTab("Automated Messages")}
          className={`px-6 py-3 text-sm font-medium transition-colors ${
            activeTab === "Automated Messages"
              ? "text-primary border-b-2 border-primary"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Automated Messages
        </button>
      </div>

      {/* WhatsApp Tab Content */}
      {activeTab === "WhatsApp" && (
        <>
          {/* WhatsApp Single Notification Section */}
          <form
            onSubmit={handleWhatsAppSingleSubmit}
            className="bg-white shadow-lg rounded-2xl p-6 space-y-4"
          >
            <h2 className="text-lg font-semibold mb-4">Single Notification</h2>

            {/* Number Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <div className="flex gap-2">
                {/* <div className="w-[120px]">
                  <Dropdown
                    options={countryCodes}
                    value={whatsappSinglePayload.countryCode}
                    onSelect={(val) =>
                      setWhatsappSinglePayload({
                        ...whatsappSinglePayload,
                        countryCode: val,
                      })
                    }
                    placeholder="+234"
                  />
                </div> */}
                <div className="flex-1">
                  <TextInput
                    id="whatsapp-phone"
                    type="text"
                    placeholder="Enter number"
                    value={whatsappSinglePayload.phoneNumber}
                    register={{
                      onChange: (e: any) =>
                        setWhatsappSinglePayload({
                          ...whatsappSinglePayload,
                          phoneNumber: e.target.value,
                        }),
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Template Type */}
            <Dropdown
              label="Template Type"
              placeholder="Select template type"
              options={templateTypes}
              value={whatsappSinglePayload.templateType}
              onSelect={(val) =>
                setWhatsappSinglePayload({
                  ...whatsappSinglePayload,
                  templateType: val,
                })
              }
            />

            <button
              type="submit"
              disabled={sendingWhatsAppSingle}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow text-white ${
                sendingWhatsAppSingle
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-primary hover:bg-primary/90"
              }`}
            >
              {sendingWhatsAppSingle ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Notification
                </>
              )}
            </button>
          </form>

          {/* WhatsApp Bulk Notification Section */}
          <form
            onSubmit={handleWhatsAppBulkSubmit}
            className="bg-white shadow-lg rounded-2xl p-6 space-y-4"
          >
            <h2 className="text-lg font-semibold mb-4">Bulk Notification</h2>

            {/* Template Type */}
            <Dropdown
              label="Template Type"
              placeholder="Select template type"
              options={templateTypes}
              value={whatsappBulkPayload.templateType}
              onSelect={(val) =>
                setWhatsappBulkPayload({
                  ...whatsappBulkPayload,
                  templateType: val,
                })
              }
            />

            <button
              type="submit"
              disabled={sendingWhatsAppBulk}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow text-white ${
                sendingWhatsAppBulk
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-primary hover:bg-primary/90"
              }`}
            >
              {sendingWhatsAppBulk ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Notification
                </>
              )}
            </button>
          </form>
        </>
      )}

      {/* Push Tab Content */}
      {activeTab === "Push" && (
        <>
          {/* Single Notification Section */}
          <form
            onSubmit={handleSingleSubmit}
            className="bg-white shadow-lg rounded-2xl p-6 space-y-4"
          >
            <h2 className="text-lg font-semibold mb-4">Single Notification</h2>

            <TextInput
              id="single-title"
              type="text"
              placeholder="Title"
              value={singlePayload.title}
              register={{
                onChange: (e: any) =>
                  setSinglePayload({ ...singlePayload, title: e.target.value }),
              }}
            />

            <Dropdown
              label="Contract"
              placeholder="Search by code"
              options={refinedOrders}
              value={singlePayload.contractId}
              onSelect={(val: any) =>
                setSinglePayload({
                  ...singlePayload,
                  contractId: val?.contractId,
                })
              }
              onSearch={(query) => setFilter(query)}
            />

            <TextArea
              id="single-text"
              placeholder="Notification text"
              value={singlePayload.text}
              onChange={(e) =>
                setSinglePayload({ ...singlePayload, text: e.target.value })
              }
            />

            <button
              type="submit"
              disabled={sendingSingle}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow text-white ${
                sendingSingle
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-primary hover:bg-primary/90"
              }`}
            >
              {sendingSingle ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Notification
                </>
              )}
            </button>
          </form>

          {/* Bulk Notification Section */}
          <form
            onSubmit={handleBulkSubmit}
            className="bg-white shadow-lg rounded-2xl p-6 space-y-4"
          >
            <h2 className="text-lg font-semibold mb-4">Bulk Notification</h2>

            <TextInput
              id="bulk-title"
              type="text"
              placeholder="Title"
              value={bulkPayload.title}
              register={{
                onChange: (e: any) =>
                  setBulkPayload({ ...bulkPayload, title: e.target.value }),
              }}
            />

            <TextArea
              id="bulk-message"
              placeholder="Message"
              value={bulkPayload.message}
              onChange={(e) =>
                setBulkPayload({ ...bulkPayload, message: e.target.value })
              }
            />

            <Dropdown
              label="Target User Type"
              options={targetUserTypes}
              value={bulkPayload.targetUserType}
              onSelect={(val) =>
                setBulkPayload({ ...bulkPayload, targetUserType: val })
              }
            />

            <button
              type="submit"
              disabled={sendingBulk}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow text-white ${
                sendingBulk
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-primary hover:bg-primary/90"
              }`}
            >
              {sendingBulk ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Notification
                </>
              )}
            </button>
          </form>
        </>
      )}

      {/* Anonymous Push Tab Content */}
      {activeTab === "Anonymous Push" && (
        <>
          {/* Anonymous Push Notification Section */}
          <form
            onSubmit={handlePromoSubmit}
            className="bg-white shadow-lg rounded-2xl p-6 space-y-4"
          >
            <h2 className="text-lg font-semibold mb-4">
              Send Promotions to Anonymous Installs
            </h2>

            <TextInput
              id="promo-title"
              type="text"
              placeholder="Title"
              value={promoPayload.title}
              register={{
                onChange: (e: any) =>
                  setPromoPayload({ ...promoPayload, title: e.target.value }),
              }}
            />

            <TextArea
              id="promo-message"
              placeholder="Message"
              value={promoPayload.message}
              onChange={(e) =>
                setPromoPayload({ ...promoPayload, message: e.target.value })
              }
            />

            <button
              type="submit"
              disabled={sendingPromo}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow text-white ${
                sendingPromo
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-primary hover:bg-primary/90"
              }`}
            >
              {sendingPromo ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Notification
                </>
              )}
            </button>
          </form>
        </>
      )}

      {/* Automated Messages Tab Content */}
      {activeTab === "Automated Messages" && <AutomatedMessages />}
    </div>
  );
};

export default PushNotificationsPage;
